/**
 * @fileoverview Incident Report Tab
 * @description Allows users to select a date range (up to 10 days) and
 *              generate a batch validation report showing Semantic and
 *              Agentic severity/priority analysis for each incident.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	Divider,
	LinearProgress,
	Paper,
	Popover,
	Snackbar,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import {
	BarChart3,
	ChevronDown,
	CheckCircle2,
	Download,
	FileText,
	FolderOpen,
	History,
	Minus,
	Play,
	RefreshCw,
	Square,
	X,
	XCircle,
} from 'lucide-react';
import { incidentApi } from '../services/incidentApi';

const MAX_DAYS = 10;
const POLL_INTERVAL_MS = 2500;
const HISTORY_PREFS_KEY = 'incident-report-history-prefs';

const SEVERITY_COLOR = {
	P1: 'error',
	P2: 'warning',
	P3: 'info',
	P4: 'success',
	P5: 'default',
	'N/A': 'default',
};

const severityChip = (label) => {
	if (!label || label === 'N/A' || label === '—') {
		return <Typography variant="caption" color="text.disabled">—</Typography>;
	}

	return (
		<Chip
			label={label}
			size="small"
			color={SEVERITY_COLOR[label] || 'default'}
			sx={{ fontWeight: 600, fontSize: '0.68rem' }}
		/>
	);
};

const matchBadge = (match) => {
	if (match === null || match === undefined) {
		return <Minus size={14} color="#9ca3af" />;
	}

	return match ? <CheckCircle2 size={16} color="#16a34a" /> : <XCircle size={16} color="#dc2626" />;
};

const priStr = (value) => (value !== null && value !== undefined ? value.toFixed(1) : '—');

const truncate = (value, length = 80) => {
	if (!value) return '—';
	return value.length > length ? `${value.slice(0, length)}…` : value;
};

const formatLocalDate = (date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, '0');
	const day = String(date.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const addDaysToIso = (isoDate, days) => {
	const [year, month, day] = isoDate.split('-').map(Number);
	const date = new Date(year, month - 1, day);
	date.setDate(date.getDate() + days);
	return formatLocalDate(date);
};

const daysBetweenInclusive = (startIso, endIso) => {
	const [sy, sm, sd] = startIso.split('-').map(Number);
	const [ey, em, ed] = endIso.split('-').map(Number);
	const startUtc = Date.UTC(sy, sm - 1, sd);
	const endUtc = Date.UTC(ey, em - 1, ed);
	return Math.floor((endUtc - startUtc) / (24 * 60 * 60 * 1000)) + 1;
};

const exportCsv = (rows) => {
	const headers = [
		'Incident ID', 'Description', 'Close Description', 'Assigned Team',
		'Open Date',
		'Severity', 'Semantic Severity', 'Agentic Severity', 'Sev Match',
		'Semantic Priority', 'Agentic Priority', 'Priority Match',
		'Overall', 'Reason',
	];

	const escapeValue = (value) => {
		const stringValue = String(value ?? '');
		return stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')
			? `"${stringValue.replace(/"/g, '""')}"`
			: stringValue;
	};

	const lines = [
		headers.join(','),
		...rows.map((row) => (
			[
				row.incident_id,
				row.description,
				row.close_description,
				row.assigned_team,
				row.open_date || '',
				row.severity,
				row.semantic_severity,
				row.agentic_severity,
				row.sev_match === true ? 'Yes' : row.sev_match === false ? 'No' : '—',
				priStr(row.semantic_priority),
				priStr(row.agentic_priority),
				row.priority_match === true ? 'Yes' : row.priority_match === false ? 'No' : '—',
				priStr(row.overall),
				row.reason,
			].map(escapeValue).join(',')
		)),
	];

	const blob = new Blob([lines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
	const url = URL.createObjectURL(blob);
	const anchor = document.createElement('a');
	anchor.href = url;
	anchor.download = `incident-report-${new Date().toISOString().slice(0, 10)}.csv`;
	anchor.click();
	URL.revokeObjectURL(url);
};

const IncidentReportTab = () => {
	const todayIso = formatLocalDate(new Date());
	const defaultStart = addDaysToIso(todayIso, -6);
	const savedHistoryPrefs = (() => {
		try {
			return JSON.parse(window.localStorage.getItem(HISTORY_PREFS_KEY) || 'null') || {};
		} catch {
			return {};
		}
	})();

	const [startDate, setStartDate] = useState(defaultStart);
	const [endDate, setEndDate] = useState(todayIso);
	const [phase, setPhase] = useState('idle');
	const [progress, setProgress] = useState({ completed: 0, total: 0 });
	const [results, setResults] = useState([]);
	const [error, setError] = useState(null);
	const [statusMessage, setStatusMessage] = useState('');
	const [expandedRow, setExpandedRow] = useState(null);
	const [showCompletionToast, setShowCompletionToast] = useState(false);
	const [isStopping, setIsStopping] = useState(false);
	const [stopDialogOpen, setStopDialogOpen] = useState(false);
	const [history, setHistory] = useState([]);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [historyExpanded, setHistoryExpanded] = useState(false);
	const [historyAnchorEl, setHistoryAnchorEl] = useState(null);
	const [historyQuery, setHistoryQuery] = useState(savedHistoryPrefs.query ?? '');
	const [historyReportTypeFilter, setHistoryReportTypeFilter] = useState(savedHistoryPrefs.reportType ?? savedHistoryPrefs.phase ?? 'all');
	const [historyDateFilter, setHistoryDateFilter] = useState(savedHistoryPrefs.date ?? 'all');
	const [selectedHistoryRun, setSelectedHistoryRun] = useState(null);
	const [selectedHistoryRows, setSelectedHistoryRows] = useState([]);
	const [selectedHistoryMeta, setSelectedHistoryMeta] = useState(null);
	const [selectedHistoryOpen, setSelectedHistoryOpen] = useState(false);

	const pollRef = useRef(null);
	const activeRunIdRef = useRef(null);
	// Guard: prevents a brief idle response right after generation starts from wiping the bar
	const generationActiveRef = useRef(false);
	// Start as true so revisiting the page on an already-complete run doesn't re-toast.
	// It is reset to false only when a brand new generation starts.
	const completionNotifiedRef = useRef(true);

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	}, []);

	const [historyError, setHistoryError] = useState(null);
	const loadHistory = useCallback(async () => {
		try {
			setHistoryLoading(true);
			setHistoryError(null);
			const data = await incidentApi.reportHistory({ limit: 100 });
			setHistory(data.jobs || []);
		} catch {
			setHistoryError('Failed to load reports. Click Refresh to retry.');
		} finally {
			setHistoryLoading(false);
		}
	}, []);

	const saveHistoryPrefs = useCallback((nextPrefs) => {
		try {
			window.localStorage.setItem(HISTORY_PREFS_KEY, JSON.stringify(nextPrefs));
		} catch {
			// Best-effort only.
		}
	}, []);

	useEffect(() => {
		saveHistoryPrefs({
			expanded: historyExpanded,
			query: historyQuery,
			reportType: historyReportTypeFilter,
			date: historyDateFilter,
		});
	}, [historyExpanded, historyQuery, historyReportTypeFilter, historyDateFilter, saveHistoryPrefs]);

	const syncReportStatus = useCallback(async () => {
		const status = await incidentApi.reportStatus();
		const wasGenerationActive = generationActiveRef.current;
		const statusRunId = Number(status.run_id || 0);
		const trackedRunId = activeRunIdRef.current ? Number(activeRunIdRef.current) : statusRunId;
		const isTrackedRun = !trackedRunId || !statusRunId || statusRunId === trackedRunId;
		setProgress({ completed: status.completed || 0, total: status.total || 0 });

		if (status.phase === 'idle') {
			// If we just kicked off a generation, a brief idle response is a race condition —
			// ignore it so the progress bar doesn't flicker away.
			if (!generationActiveRef.current) {
				setPhase('idle');
				setStatusMessage('');
				setResults([]);
			}
			return status;
		}

		// Clear the guard once we see a real non-idle phase
		if (isTrackedRun && (status.phase === 'done' || status.phase === 'error' || status.phase === 'cancelled')) {
			if (!wasGenerationActive) {
				// Stale terminal state from a previous session — don't adopt it
				return { ...status, phase: status.phase, run_id: statusRunId, isTrackedRun };
			}
			generationActiveRef.current = false;
			setIsStopping(false);
			activeRunIdRef.current = null;
		}

		const computedDone = !status.running
			&& (status.total || 0) > 0
			&& (status.completed || 0) >= (status.total || 0);

		const normalizedPhase = status.phase || (status.running ? 'running' : 'idle');
		const cancelPending = Boolean(status.cancel_requested) || normalizedPhase === 'cancelling';
		const effectivePhase = cancelPending
			? 'cancelling'
			: (normalizedPhase === 'idle' && computedDone ? 'done' : normalizedPhase);
		const liveSession = wasGenerationActive || status.running || normalizedPhase === 'running' || normalizedPhase === 'analyzing' || normalizedPhase === 'waiting' || normalizedPhase === 'cancelling';

		setPhase(effectivePhase);
		setError(status.error || null);

		if (liveSession) {
			// Only hydrate the live table for the active session, not for stale persisted runs.
			const data = await incidentApi.reportResults(trackedRunId ? { run_id: trackedRunId } : {});
			setResults(data.results || []);
		} else {
			setResults([]);
		}

		if (effectivePhase === 'cancelling') {
			setStatusMessage('Stopping report generation...');
		} else if (effectivePhase === 'waiting') {
			setStatusMessage('Waiting for AI analysis engine to finish initializing. This usually takes 1–2 minutes after server startup...');
		} else if (effectivePhase === 'running' || effectivePhase === 'analyzing' || status.running) {
			setStatusMessage(`Report generation is in progress: ${status.completed || 0} of ${status.total || 0} incidents completed.`);
		} else if (effectivePhase === 'cancelled') {
			setStatusMessage(`Report generation stopped at ${status.completed || 0} of ${status.total || 0} incidents.`);
		} else if (effectivePhase === 'done') {
			setStatusMessage(`Report generation is complete. ${status.completed || 0} incidents processed.`);
		} else if (effectivePhase === 'error' && status.error) {
			setStatusMessage('');
		}

		return { ...status, phase: effectivePhase, run_id: statusRunId, isTrackedRun };
	}, []);

	const startPolling = useCallback(() => {
		if (pollRef.current) {
			return;
		}

		pollRef.current = setInterval(async () => {
			try {
				const status = await syncReportStatus();

				// Stop only on terminal states; ignore 'idle' while generation is active
				if (status.isTrackedRun && (status.phase === 'done' || status.phase === 'error' || status.phase === 'cancelled')) {
					stopPolling();
				} else if (status.phase === 'idle' && !generationActiveRef.current) {
					stopPolling();
				}
			} catch (pollError) {
				stopPolling();
				setPhase('error');
				setStatusMessage('');
				setError(pollError.message || 'Polling failed.');
			}
		}, POLL_INTERVAL_MS);
	}, [stopPolling, syncReportStatus]);

	useEffect(() => {
		let isMounted = true;

		const refreshStatus = async () => {
			try {
				const status = await syncReportStatus();

				if (!isMounted) {
					return;
				}

				if (status.phase === 'running' || status.phase === 'analyzing' || status.phase === 'waiting' || status.running) {
					// Ensure phase is 'running' immediately so progress bar is visible
					setPhase(status.phase === 'waiting' ? 'waiting' : 'running');
					startPolling();
				} else {
					stopPolling();
				}
			} catch (syncError) {
				if (!isMounted) {
					return;
				}

				setPhase('error');
				setStatusMessage('');
				setError(syncError.message || 'Failed to load report status.');
			}
		};

		const handleVisibilityChange = () => {
			if (document.visibilityState === 'hidden') {
				// Tab is hidden — stop polling to save resources
				stopPolling();
			} else if (document.visibilityState === 'visible') {
				// Tab is visible again — re-sync and resume if still running
				refreshStatus();
			}
		};

		refreshStatus();
		loadHistory();
		window.addEventListener('focus', refreshStatus);
		document.addEventListener('visibilitychange', handleVisibilityChange);

		return () => {
			isMounted = false;
			window.removeEventListener('focus', refreshStatus);
			document.removeEventListener('visibilitychange', handleVisibilityChange);
			stopPolling();
		};
	}, [startPolling, stopPolling, syncReportStatus, loadHistory]);

	const validateGenerateDateRange = useCallback(() => {
		if (!startDate || !endDate) {
			return { error: 'Select both start date and end date.' };
		}

		let normalizedStart = startDate;
		let normalizedEnd = endDate;
		let swapped = false;

		if (normalizedStart > normalizedEnd) {
			normalizedStart = endDate;
			normalizedEnd = startDate;
			swapped = true;
		}

		if (normalizedStart > todayIso || normalizedEnd > todayIso) {
			return { error: 'Future dates are not allowed.' };
		}

		if (normalizedEnd < normalizedStart) {
			return { error: 'End date must be on or after start date.' };
		}

		const selectedDays = daysBetweenInclusive(normalizedStart, normalizedEnd);
		if (selectedDays > MAX_DAYS) {
			return { error: `You can select a maximum of ${MAX_DAYS} days.` };
		}

		return { error: null, normalizedStart, normalizedEnd, swapped };
	}, [endDate, startDate, todayIso]);

	const initializeGenerateState = useCallback(() => {
		stopPolling();
		generationActiveRef.current = true;
		completionNotifiedRef.current = false;
		setShowCompletionToast(false);
		setPhase('running');
		setError(null);
		setStatusMessage('');
		setResults([]);
		activeRunIdRef.current = null;
		setSelectedHistoryRun(null);
		setSelectedHistoryRows([]);
		setSelectedHistoryMeta(null);
		setSelectedHistoryOpen(false);
		setProgress({ completed: 0, total: 0 });
		setExpandedRow(null);
	}, [stopPolling]);

	const syncAlreadyRunningReport = useCallback(async () => {
		setError(null);
		setStatusMessage('A report is already running. Showing live progress for the current report.');
		try {
			const status = await syncReportStatus();
			if (status.phase === 'running' || status.phase === 'analyzing' || status.running) {
				startPolling();
			}
		} catch (syncError) {
			setPhase('error');
			setStatusMessage('');
			setError(syncError.message || 'Failed to load running report status.');
		}
	}, [startPolling, syncReportStatus]);

	const handleGenerateError = useCallback(async (requestError) => {
		if (requestError.status === 409 || requestError.message === 'A report is already being generated.') {
			await syncAlreadyRunningReport();
			return;
		}

		if (requestError.status === 503 && requestError.details?.code === 'COLLECTION_BUILDING') {
			setPhase('idle');
			generationActiveRef.current = false;
			setError('The AI analysis engine is initializing after a server restart. Please try again in a minute or two.');
			return;
		}

		setPhase('error');
		generationActiveRef.current = false;
		setError(requestError.message || 'Failed to start report generation.');
	}, [syncAlreadyRunningReport]);

	const handleGenerate = useCallback(async () => {
		const validation = validateGenerateDateRange();
		if (validation.error) {
			setError(validation.error);
			return;
		}

		if (validation.swapped) {
			setStartDate(validation.normalizedStart);
			setEndDate(validation.normalizedEnd);
		}

		initializeGenerateState();

		try {
			await incidentApi.generateReport({
				start_date: validation.normalizedStart,
				end_date: validation.normalizedEnd,
			});
			const startedStatus = await incidentApi.reportStatus();
			if (startedStatus?.run_id) {
				activeRunIdRef.current = Number(startedStatus.run_id);
			}
			loadHistory();
			startPolling();
		} catch (requestError) {
			await handleGenerateError(requestError);
		}
	}, [handleGenerateError, initializeGenerateState, loadHistory, startPolling, validateGenerateDateRange]);

	const handleStop = useCallback(async ({ downloadAfterStop = false } = {}) => {
		try {
			setIsStopping(true);
			setError(null);
			setPhase('cancelling');
			setStatusMessage('Stopping report generation...');
			await incidentApi.cancelReport();
			setStopDialogOpen(false);

			const status = await syncReportStatus();
			if (status.phase === 'cancelling' || status.phase === 'running' || status.phase === 'analyzing' || status.running) {
				startPolling();
			}

			if (downloadAfterStop) {
				try {
					const latest = await incidentApi.reportResults();
					const partialRows = latest.results || [];
					setResults(partialRows);
					if (partialRows.length > 0) {
						exportCsv(partialRows);
					}
				} catch {
					// Best effort export after stop; ignore fetch failures here.
				}
			}
			setIsStopping(false);
		} catch (requestError) {
			if (requestError?.status === 409) {
				setStopDialogOpen(false);
				try {
					const status = await syncReportStatus();
					if (status.phase === 'running' || status.phase === 'analyzing' || status.phase === 'cancelling' || status.running) {
						setError('Unable to stop this run right now. Please retry in a few seconds.');
						startPolling();
					} else {
						generationActiveRef.current = false;
						setError(null);
						setStatusMessage('No active report is running.');
						stopPolling();
					}
				} catch {
					setError(requestError.message || 'Failed to stop report generation.');
				}
				setIsStopping(false);
				return;
			}

			setIsStopping(false);
			setError(requestError.message || 'Failed to stop report generation.');
		}
	}, [startPolling, stopPolling, syncReportStatus]);

	const handleStopClick = useCallback(() => {
		setStopDialogOpen(true);
	}, []);

	const handleReset = () => {
		stopPolling();
		setPhase('idle');
		activeRunIdRef.current = null;
		setIsStopping(false);
		setStopDialogOpen(false);
		setShowCompletionToast(false);
		completionNotifiedRef.current = false;
		setError(null);
		setStatusMessage('');
		setResults([]);
		setProgress({ completed: 0, total: 0 });
		setExpandedRow(null);
		setStartDate(defaultStart);
		setEndDate(todayIso);
	};

	const handleOpenHistoryRun = useCallback(async (runId) => {
		if (!runId) return;

		try {
			setError(null);
			const historyJob = history.find((job) => Number(job.run_id) === Number(runId)) || null;

			const data = await incidentApi.reportResults({ run_id: runId });
			setSelectedHistoryRun(runId);
			setSelectedHistoryRows(data.results || []);
			setSelectedHistoryMeta({
				phase: data.phase || historyJob?.phase || 'done',
				total: data.total || historyJob?.total || 0,
				completed: data.completed || historyJob?.completed || 0,
				start_date: historyJob?.start_date || null,
				end_date: historyJob?.end_date || null,
			});
			setSelectedHistoryOpen(true);
			setStatusMessage(`Loaded saved report run #${runId} in My Reports.`);
		} catch (requestError) {
			setError(requestError.message || `Failed to load report run #${runId}.`);
		}
	}, [history]);

	const handleDownloadHistoryRun = useCallback(async (runId) => {
		if (!runId) return;
		try {
			const data = await incidentApi.reportResults({ run_id: runId });
			const rows = data.results || [];
			if (rows.length > 0) {
				exportCsv(rows);
				return;
			}
			setError(`No rows available to download for run #${runId}.`);
		} catch (requestError) {
			setError(requestError.message || `Failed to download run #${runId}.`);
		}
	}, []);

	const handleCloseHistoryRun = useCallback(() => {
		setSelectedHistoryRun(null);
		setSelectedHistoryRows([]);
		setSelectedHistoryMeta(null);
		setSelectedHistoryOpen(false);
	}, []);

	const filteredHistory = history.filter((job) => {
		const query = historyQuery.trim().toLowerCase();
		const matchesQuery = !query
			|| String(job.run_id || '').includes(query)
			|| String(job.report_type || job.phase || '').toLowerCase().includes(query)
			|| String(job.start_date || '').includes(query)
			|| String(job.end_date || '').includes(query);

		const jobReportType = String(job.report_type || 'incident_validation_report').toLowerCase();
		const matchesReportType = historyReportTypeFilter === 'all' || jobReportType === historyReportTypeFilter.toLowerCase();

		let matchesDate = true;
		if (historyDateFilter !== 'all') {
			const jobDate = String(job.updated_at || job.created_at || '').slice(0, 10);
			if (historyDateFilter === 'today') {
				matchesDate = jobDate === todayIso;
			} else if (historyDateFilter === '7d') {
				const cutoff = addDaysToIso(todayIso, -6);
				matchesDate = !!jobDate && jobDate >= cutoff;
			} else if (historyDateFilter === '30d') {
				const cutoff = addDaysToIso(todayIso, -29);
				matchesDate = !!jobDate && jobDate >= cutoff;
			}
		}

		return matchesQuery && matchesReportType && matchesDate;
	});

	const isRunning = phase === 'running' || phase === 'analyzing' || phase === 'waiting';
	const isCancelled = phase === 'cancelled';
	const isComplete = phase === 'done' || (!isRunning && !isCancelled && progress.total > 0 && progress.completed >= progress.total);
	const maxEndDate = (() => {
		const fromStart = addDaysToIso(startDate, MAX_DAYS - 1);
		return fromStart < todayIso ? fromStart : todayIso;
	})();
	const percentComplete = progress.total > 0
		? Math.round((progress.completed / progress.total) * 100)
		: 0;
	const progressHeading = phase === 'waiting'
		? 'Initializing AI engine…'
		: (isRunning ? 'Analysing incidents…' : 'Report Complete');
	const progressDetail = phase === 'waiting'
		? 'The AI analysis engine is loading. Your report will start automatically once ready.'
		: `${percentComplete}% complete — ${isRunning ? 'results appear row by row as each incident is processed' : ''}`;

	useEffect(() => {
		if (isComplete) {
			if (!completionNotifiedRef.current) {
				setShowCompletionToast(true);
				loadHistory();
				completionNotifiedRef.current = true;
			}
		}
	}, [isComplete, loadHistory]);

	return (
		<Box sx={{ p: { xs: 1.5, md: 2 } }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
				<BarChart3 size={22} color="#01426A" />
				<Box>
					<Typography variant="subtitle1" fontWeight={700} color="#01426A" lineHeight={1.2}>
						Incident Validation Report
					</Typography>
					<Typography variant="caption" color="text.secondary">
					Generate a semantic & agentic severity/priority validation report for all incidents.
					</Typography>
				</Box>
			</Box>

			<Box>
				<Stack direction="row" justifyContent="flex-end" sx={{ mb: 1, pr: 0.5 }}>
					<Tooltip title={historyExpanded ? 'Hide My Reports' : 'Show My Reports'}>
						<Button
							variant="text"
							size="small"
							onClick={(event) => {
								const next = !historyExpanded;
								setHistoryExpanded(next);
								setHistoryAnchorEl(next ? event.currentTarget : null);
								if (next) loadHistory();
							}}
							startIcon={historyExpanded ? <X size={16} /> : <History size={16} />}
							sx={{ 
								textTransform: 'none', 
								minWidth: 0, 
								px: 0.75,
								color: '#01426A',
								'&:hover': { bgcolor: 'rgba(1, 66, 106, 0.08)' }
							}}
						>
						</Button>
					</Tooltip>
				</Stack>

					<Popover
						open={historyExpanded}
						anchorEl={historyAnchorEl}
						onClose={() => {
							setHistoryExpanded(false);
							setHistoryAnchorEl(null);
						}}
						anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
						transformOrigin={{ vertical: 'top', horizontal: 'right' }}
						PaperProps={{
							sx: {
								p: 1.5,
								mt: 1,
								borderRadius: 2,
								border: '1px solid #e5e7eb',
								width: { xs: '92vw', sm: 380 },
								maxHeight: 520,
								overflowY: 'auto',
								overflowX: 'hidden',
								boxShadow: '0 8px 24px rgba(0, 0, 0, 0.18)',
							},
						}}
					>
						<Stack spacing={1}>
								<Stack direction="row" spacing={1} alignItems="center">
									<History size={16} color="#01426A" />
									<Typography variant="body2" fontWeight={700} color="#01426A">
										My Reports
									</Typography>
								</Stack>
								<Stack spacing={1}>
									<TextField
										label="Search reports"
										size="small"
										placeholder="Run #, report type, or date"
										value={historyQuery}
										onChange={(event) => setHistoryQuery(event.target.value)}
										InputProps={{ sx: { borderRadius: 1.5 } }}
									/>
									<Stack direction="row" gap={1} flexWrap="wrap">
										<TextField
											select
											label="Report type"
											size="small"
											value={historyReportTypeFilter}
											onChange={(event) => setHistoryReportTypeFilter(event.target.value)}
											SelectProps={{ native: true }}
											sx={{ minWidth: 170 }}
										>
											<option value="all">All</option>
											<option value="incident_validation_report">Incident validation report</option>
										</TextField>
										<TextField
											select
											label="Date"
											size="small"
											value={historyDateFilter}
											onChange={(event) => setHistoryDateFilter(event.target.value)}
											SelectProps={{ native: true }}
											sx={{ minWidth: 120 }}
										>
											<option value="all">All time</option>
											<option value="today">Today</option>
											<option value="7d">Last 7 days</option>
											<option value="30d">Last 30 days</option>
										</TextField>
									</Stack>
								</Stack>

								<Stack direction="row" justifyContent="flex-end">
									<Button
										variant="text"
										size="small"
										startIcon={historyLoading ? <CircularProgress size={12} /> : <RefreshCw size={14} />}
										onClick={loadHistory}
										disabled={historyLoading}
										sx={{ textTransform: 'none' }}
									>
										Refresh
									</Button>
								</Stack>

								{historyError ? (
									<Typography variant="caption" color="error">
										{historyError}
									</Typography>
								) : historyLoading ? (
									<Stack alignItems="center" py={2}>
										<CircularProgress size={20} />
									</Stack>
								) : history.length === 0 ? (
									<Typography variant="caption" color="text.secondary">
										No saved report runs yet.
									</Typography>
								) : (
									<Stack spacing={1}>
										{filteredHistory.length === 0 ? (
											<Typography variant="caption" color="text.secondary">
												No matching report runs.
											</Typography>
										) : filteredHistory.map((job) => (
											<Stack
												key={job.run_id}
												sx={{
													border: '1px solid #e5e7eb',
													borderRadius: 1.5,
													px: 1.25,
													py: 1,
													gap: 0.9,
												}}
											>
												<Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
													<Typography variant="caption" sx={{ fontWeight: 700, color: '#01426A' }}>
														Run #{job.run_id}
													</Typography>
													<Chip label={job.report_type || 'Incident validation report'} size="small" />
													<Typography variant="caption" color="text.secondary">
														{job.completed || 0}/{job.total || 0}
													</Typography>
												</Stack>

												{job.start_date && job.end_date && (
													<Typography variant="caption" color="text.secondary">
														{job.start_date} to {job.end_date}
													</Typography>
												)}

												<Stack direction="row" spacing={1}>
													<Button
														variant="outlined"
														size="small"
														startIcon={<FolderOpen size={13} />}
														onClick={() => handleOpenHistoryRun(job.run_id)}
														sx={{ textTransform: 'none' }}
													>
														Open
													</Button>
													<Button
														variant="outlined"
														size="small"
														startIcon={<Download size={13} />}
														onClick={() => handleDownloadHistoryRun(job.run_id)}
														sx={{ textTransform: 'none' }}
													>
														Download
													</Button>
												</Stack>
											</Stack>
										))}
								</Stack>
								)}
							</Stack>
				</Popover>

			</Box>

			<Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
				<Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" flexWrap="wrap">
					<Stack direction={{ xs: 'column', sm: 'row' }} gap={1.2} flexWrap="wrap">
						<TextField
							label="Start Date"
							type="date"
							size="small"
							value={startDate}
							onChange={(event) => {
								const nextStart = event.target.value;
								setStartDate(nextStart);
								if (nextStart > endDate) {
									setEndDate(nextStart);
									return;
								}
								const maxAllowedEnd = (() => {
									const byRange = addDaysToIso(nextStart, MAX_DAYS - 1);
									return byRange < todayIso ? byRange : todayIso;
								})();
								if (endDate > maxAllowedEnd) {
									setEndDate(maxAllowedEnd);
								}
							}}
							InputLabelProps={{ shrink: true }}
							inputProps={{ max: todayIso }}
							disabled={isRunning}
							sx={{ minWidth: 170 }}
						/>
						<TextField
							label="End Date"
							type="date"
							size="small"
							value={endDate}
							onChange={(event) => {
								const nextEnd = event.target.value;
								if (nextEnd > todayIso) {
									setEndDate(todayIso);
									return;
								}
								if (nextEnd < startDate) {
									setEndDate(startDate);
									return;
								}
								const maxAllowedEnd = (() => {
									const byRange = addDaysToIso(startDate, MAX_DAYS - 1);
									return byRange < todayIso ? byRange : todayIso;
								})();
								setEndDate(nextEnd > maxAllowedEnd ? maxAllowedEnd : nextEnd);
							}}
							InputLabelProps={{ shrink: true }}
							inputProps={{ min: startDate, max: maxEndDate }}
							disabled={isRunning}
							sx={{ minWidth: 170 }}
						/>
						<Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
							Maximum {MAX_DAYS} days per report.
						</Typography>
					</Stack>

					<Stack direction="row" gap={1} justifyContent="flex-end" flexWrap="wrap">
					<Button
						variant="contained"
						startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : <Play size={15} />}
						onClick={handleGenerate}
						disabled={isRunning || isStopping}
						size="small"
						sx={{
							bgcolor: '#01426A',
							'&:hover': { bgcolor: '#012d49' },
							textTransform: 'none',
							fontWeight: 600,
						}}
					>
						{isRunning ? 'Generating…' : 'Generate Report'}
					</Button>

					{isRunning && (
						<Button
							variant="outlined"
							color="error"
							startIcon={isStopping ? <CircularProgress size={14} color="inherit" /> : <Square size={14} />}
							onClick={handleStopClick}
							disabled={isStopping}
							size="small"
							sx={{ textTransform: 'none' }}
						>
							{isStopping ? 'Stopping…' : 'Stop'}
						</Button>
					)}

					{(isComplete || isCancelled || phase === 'error') && (
						<Button
							variant="outlined"
							startIcon={<RefreshCw size={14} />}
							onClick={handleReset}
							size="small"
							sx={{ textTransform: 'none' }}
						>
							Reset
						</Button>
					)}
					</Stack>
				</Stack>
			</Paper>

			{selectedHistoryRun && (
				<Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
					<Accordion
						expanded={selectedHistoryOpen}
						onChange={(_, expanded) => setSelectedHistoryOpen(expanded)}
						disableGutters
						sx={{
							border: '1px solid #dbe3ea',
							borderRadius: 2,
							bgcolor: '#f8fafc',
							'&:before': { display: 'none' },
						}}
					>
						<AccordionSummary expandIcon={<ChevronDown size={16} />}>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%' }} gap={1}>
								<Box>
									<Typography variant="body2" fontWeight={700} color="#01426A">
										Run #{selectedHistoryRun} records
									</Typography>
									<Typography variant="caption" color="text.secondary">
										{selectedHistoryMeta?.completed || 0}/{selectedHistoryMeta?.total || 0} incidents • Incident validation report
									</Typography>
								</Box>
								<Button variant="text" size="small" onClick={(event) => { event.stopPropagation(); handleCloseHistoryRun(); }} sx={{ textTransform: 'none' }}>
									Close records
								</Button>
							</Stack>
						</AccordionSummary>
						<AccordionDetails>
							{selectedHistoryRows.length === 0 ? (
								<Typography variant="caption" color="text.secondary">No records available.</Typography>
							) : (
								<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 480, overflow: 'auto' }}>
									<Table size="small" stickyHeader>
										<TableHead>
											<TableRow sx={{ '& th': { bgcolor: '#01426A', color: '#fff', fontWeight: 700, fontSize: '0.72rem', py: 1, whiteSpace: 'nowrap' } }}>
												<TableCell>Incident ID</TableCell>
												<TableCell>Description</TableCell>
												<TableCell>Close Description</TableCell>
												<TableCell>Assigned Team</TableCell>
												<TableCell>Open Date</TableCell>
												<TableCell align="center">Severity</TableCell>
												<TableCell align="center">Semantic Sev</TableCell>
												<TableCell align="center">Agentic Sev</TableCell>
												<TableCell align="center">Sev Match</TableCell>
												<TableCell align="center">Semantic Pri</TableCell>
												<TableCell align="center">Agentic Pri</TableCell>
												<TableCell align="center">Pri Match</TableCell>
												<TableCell align="center">Overall</TableCell>
												<TableCell>Reason</TableCell>
											</TableRow>
										</TableHead>
										<TableBody>
											{selectedHistoryRows.map((row) => (
												<TableRow key={`history-main-${selectedHistoryRun}-${row.incident_id}`} hover sx={{ '&:hover': { bgcolor: '#f0f9ff' } }}>
													<TableCell sx={{ fontWeight: 700, color: '#01426A', whiteSpace: 'nowrap' }}>#{row.incident_id}</TableCell>
													<TableCell sx={{ maxWidth: 200 }}>
														<Tooltip title={row.description || ''} placement="top">
															<Typography variant="caption" noWrap display="block">{truncate(row.description, 60)}</Typography>
														</Tooltip>
													</TableCell>
													<TableCell sx={{ maxWidth: 200 }}>
														<Tooltip title={row.close_description || ''} placement="top">
															<Typography variant="caption" noWrap display="block">{truncate(row.close_description, 60)}</Typography>
														</Tooltip>
													</TableCell>
													<TableCell>
														<Typography variant="caption" noWrap display="block" sx={{ maxWidth: 150 }}>{row.assigned_team || '—'}</Typography>
													</TableCell>
													<TableCell>
														<Typography variant="caption" noWrap display="block">{row.open_date ? row.open_date.slice(0, 10) : '—'}</Typography>
													</TableCell>
													<TableCell align="center">{severityChip(row.severity)}</TableCell>
													<TableCell align="center">{row.error ? <Typography variant="caption" color="error">Error</Typography> : severityChip(row.semantic_severity)}</TableCell>
													<TableCell align="center">{row.error ? <Typography variant="caption" color="error">—</Typography> : severityChip(row.agentic_severity)}</TableCell>
													<TableCell align="center">{row.error ? <Minus size={14} color="#9ca3af" /> : matchBadge(row.sev_match)}</TableCell>
													<TableCell align="center"><Typography variant="caption">{row.error ? '—' : priStr(row.semantic_priority)}</Typography></TableCell>
													<TableCell align="center"><Typography variant="caption">{row.error ? '—' : priStr(row.agentic_priority)}</Typography></TableCell>
													<TableCell align="center">{row.error ? <Minus size={14} color="#9ca3af" /> : matchBadge(row.priority_match)}</TableCell>
													<TableCell align="center"><Typography variant="caption" fontWeight={600}>{row.error ? '—' : priStr(row.overall)}</Typography></TableCell>
													<TableCell sx={{ maxWidth: 240 }}>
														<Tooltip title={row.reason || ''} placement="top-start">
															<Typography variant="caption" noWrap display="block">{truncate(row.reason, 80)}</Typography>
														</Tooltip>
													</TableCell>
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>
							)}
						</AccordionDetails>
					</Accordion>
				</Paper>
			)}

			<>
			<Collapse in={phase === 'error' && !!error}>
				<Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
					{error}
				</Alert>
			</Collapse>

			<Collapse in={(isRunning || phase === 'cancelling') && !!statusMessage}>
				<Alert severity="info" sx={{ mb: 2 }}>
					{statusMessage}
				</Alert>
			</Collapse>

			<Collapse in={isComplete}>
				<Alert
					severity="success"
					sx={{ mb: 2 }}
					action={results.length > 0 ? (
						<Button
							color="success"
							variant="contained"
							size="small"
							startIcon={<Download size={14} />}
							onClick={() => exportCsv(results)}
							sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
						>
							Download Report
						</Button>
					) : undefined}
				>
					<strong>Report ready!</strong> {progress.completed || results.length || 0} incident{(progress.completed || results.length || 0) !== 1 ? 's' : ''} analysed.
					{results.length > 0 ? ' Click Download to export as CSV.' : ' No export rows are available for this run.'}
				</Alert>
			</Collapse>

			{(isRunning || isComplete) && (
				<Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
					<Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
						<Stack direction="row" spacing={2} alignItems="center">
							<Typography variant="body2" fontWeight={600}>
								{progressHeading}
							</Typography>
							{phase !== 'waiting' && (
								<Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
									Processed: {progress.completed} / {progress.total} incidents
								</Typography>
							)}
						</Stack>
					</Stack>
					<LinearProgress
						variant={phase === 'waiting' || progress.total <= 0 ? 'indeterminate' : 'determinate'}
						value={percentComplete}
						sx={{ borderRadius: 4, height: 6 }}
					/>
					<Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
						{progressDetail}
					</Typography>
				</Paper>
			)}

			<Snackbar
				open={showCompletionToast}
				autoHideDuration={8000}
				onClose={() => setShowCompletionToast(false)}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<Alert severity="success" variant="filled" onClose={() => setShowCompletionToast(false)} sx={{ width: '100%' }}>
					Report generation completed successfully.
				</Alert>
			</Snackbar>

			<Dialog
				open={stopDialogOpen}
				onClose={() => !isStopping && setStopDialogOpen(false)}
				fullWidth
				maxWidth="xs"
			>
				<DialogTitle>Stop Report Generation?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Choose how you want to proceed.
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button
						onClick={() => setStopDialogOpen(false)}
						disabled={isStopping}
						variant="text"
						sx={{ textTransform: 'none' }}
					>
						Cancel
					</Button>
					<Button
						onClick={() => handleStop({ downloadAfterStop: true })}
						disabled={isStopping}
						variant="outlined"
						startIcon={isStopping ? <CircularProgress size={14} /> : <Download size={14} />}
						sx={{ textTransform: 'none' }}
					>
						Download So Far
					</Button>
					<Button
						onClick={() => handleStop({ downloadAfterStop: false })}
						disabled={isStopping}
						color="error"
						variant="contained"
						startIcon={isStopping ? <CircularProgress size={14} color="inherit" /> : <Square size={14} />}
						sx={{ textTransform: 'none' }}
					>
						Confirm Stop
					</Button>
				</DialogActions>
			</Dialog>

			{results.length > 0 && (
				<>
					<Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
						<Stack direction="row" spacing={1} alignItems="center">
							<FileText size={16} color="#01426A" />
							<Typography variant="body2" fontWeight={600}>
								{results.length} incident{results.length !== 1 ? 's' : ''}
								{isComplete && ' — report complete'}
								{phase === 'running' && ' so far'}
							</Typography>
						</Stack>
					</Stack>

					<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, maxHeight: 600, overflow: 'auto' }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow
									sx={{
										'& th': {
											bgcolor: '#01426A',
											color: '#fff',
											fontWeight: 700,
											fontSize: '0.72rem',
											py: 1,
											whiteSpace: 'nowrap',
										},
									}}
								>
									<TableCell>Incident ID</TableCell>
									<TableCell>Description</TableCell>
									<TableCell>Close Description</TableCell>
									<TableCell>Assigned Team</TableCell>
									<TableCell>Open Date</TableCell>
									<TableCell align="center">Severity</TableCell>
									<TableCell align="center">Semantic Sev</TableCell>
									<TableCell align="center">Agentic Sev</TableCell>
									<TableCell align="center">Sev Match</TableCell>
									<TableCell align="center">Semantic Pri</TableCell>
									<TableCell align="center">Agentic Pri</TableCell>
									<TableCell align="center">Pri Match</TableCell>
									<TableCell align="center">Overall</TableCell>
									<TableCell>Reason</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{results.map((row) => (
									<React.Fragment key={row.incident_id}>
										<TableRow
											hover
											sx={{
												cursor: 'pointer',
												bgcolor: row.error ? '#fff5f5' : 'inherit',
												'&:hover': { bgcolor: '#f0f9ff' },
											}}
											onClick={() => setExpandedRow((previousRow) => (previousRow === row.incident_id ? null : row.incident_id))}
										>
											<TableCell sx={{ fontWeight: 700, color: '#01426A', whiteSpace: 'nowrap' }}>
												#{row.incident_id}
											</TableCell>
											<TableCell sx={{ maxWidth: 200 }}>
												<Tooltip title={row.description || ''} placement="top">
													<Typography variant="caption" noWrap display="block">
														{truncate(row.description, 60)}
													</Typography>
												</Tooltip>
											</TableCell>
											<TableCell sx={{ maxWidth: 200 }}>
												<Tooltip title={row.close_description || ''} placement="top">
													<Typography variant="caption" noWrap display="block">
														{truncate(row.close_description, 60)}
													</Typography>
												</Tooltip>
											</TableCell>
											<TableCell>
												<Typography variant="caption" noWrap display="block" sx={{ maxWidth: 150 }}>
													{row.assigned_team || '—'}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="caption" noWrap display="block">
													{row.open_date ? row.open_date.slice(0, 10) : '—'}
												</Typography>
											</TableCell>
											<TableCell align="center">{severityChip(row.severity)}</TableCell>
											<TableCell align="center">
												{row.error ? <Typography variant="caption" color="error">Error</Typography> : severityChip(row.semantic_severity)}
											</TableCell>
											<TableCell align="center">
												{row.error ? <Typography variant="caption" color="error">—</Typography> : severityChip(row.agentic_severity)}
											</TableCell>
											<TableCell align="center">{row.error ? <Minus size={14} color="#9ca3af" /> : matchBadge(row.sev_match)}</TableCell>
											<TableCell align="center">
												<Typography variant="caption">{row.error ? '—' : priStr(row.semantic_priority)}</Typography>
											</TableCell>
											<TableCell align="center">
												<Typography variant="caption">{row.error ? '—' : priStr(row.agentic_priority)}</Typography>
											</TableCell>
											<TableCell align="center">{row.error ? <Minus size={14} color="#9ca3af" /> : matchBadge(row.priority_match)}</TableCell>
											<TableCell align="center">
												<Typography variant="caption" fontWeight={600}>{row.error ? '—' : priStr(row.overall)}</Typography>
											</TableCell>
											<TableCell sx={{ maxWidth: 240 }}>
												<Tooltip title={row.reason || ''} placement="top-start">
													<Typography variant="caption" noWrap display="block">
														{truncate(row.reason, 80)}
													</Typography>
												</Tooltip>
											</TableCell>
										</TableRow>

										<TableRow>
											<TableCell colSpan={14} sx={{ p: 0, border: 0 }}>
												<Collapse in={expandedRow === row.incident_id} unmountOnExit>
													<Box sx={{ bgcolor: '#f8fafc', px: 3, py: 2, borderBottom: '1px solid #e5e7eb' }}>
														<Stack spacing={1}>
															{row.error && <Alert severity="error" sx={{ py: 0 }}>{row.error}</Alert>}
															<Box>
																<Typography variant="caption" fontWeight={700} color="text.secondary">
																	DESCRIPTION
																</Typography>
																<Typography variant="body2" sx={{ mt: 0.25 }}>
																	{row.description || '—'}
																</Typography>
															</Box>
															<Divider />
															<Box>
																<Typography variant="caption" fontWeight={700} color="text.secondary">
																	CLOSE DESCRIPTION
																</Typography>
																<Typography variant="body2" sx={{ mt: 0.25 }}>
																	{row.close_description || '—'}
																</Typography>
															</Box>
															<Divider />
															<Box>
																<Typography variant="caption" fontWeight={700} color="text.secondary">
																	AI REASON / RECOMMENDATION
																</Typography>
																<Typography variant="body2" sx={{ mt: 0.25, whiteSpace: 'pre-wrap' }}>
																	{row.reason || '—'}
																</Typography>
															</Box>
														</Stack>
													</Box>
												</Collapse>
											</TableCell>
										</TableRow>
									</React.Fragment>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}

			{phase === 'done' && results.length === 0 && (
				<Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
					<Typography variant="body2" color="text.secondary">
						No incidents found for the selected date range.
					</Typography>
				</Paper>
			)}
			</>
				</Box>
	);
	};

export default IncidentReportTab;
